import * as React from 'react';
import { cn } from '../../utils/cn';

type ErrorMessageVariant = 'error' | 'warning' | 'info';

export interface ErrorMessageProps {
  error?: string | Error | null;
  errors?: string[] | Error[];
  icon?: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
  variant?: ErrorMessageVariant;
}

const defaultIcons: Record<ErrorMessageVariant, React.ReactNode> = {
  error: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M8 5V9M8 11V11.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  warning: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
      <path
        d="M7.2 3.5C7.6 2.8 8.4 2.8 8.8 3.5L14.4 13C14.8 13.7 14.4 14.5 13.6 14.5H2.4C1.6 14.5 1.2 13.7 1.6 13L7.2 3.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M8 6V9M8 11.5V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  info: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
      <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M8 7V11M8 5V5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
};

const variantClasses: Record<ErrorMessageVariant, string> = {
  error: 'text-error border-error/20 bg-error/10',
  warning: 'text-warning border-warning/20 bg-warning/10',
  info: 'text-info border-info/20 bg-info/10',
};

export const ErrorMessage = React.forwardRef<HTMLDivElement, ErrorMessageProps>(
  ({ error, errors, icon, dismissible = false, onDismiss, className, variant = 'error' }, ref) => {
    const [isVisible, setIsVisible] = React.useState(true);

    // Normalize errors into array
    const errorList = React.useMemo(() => {
      if (errors) {
        return errors.map((e) => (e instanceof Error ? e.message : e));
      }
      if (error) {
        return [error instanceof Error ? error.message : error];
      }
      return [];
    }, [error, errors]);

    const handleDismiss = () => {
      setIsVisible(false);
      onDismiss?.();
    };

    if (!isVisible || errorList.length === 0) {
      return null;
    }

    const finalIcon = icon || defaultIcons[variant];

    return (
      <div
        ref={ref}
        role="alert"
        className={cn(
          'relative flex gap-3 p-3 rounded-lg border',
          variantClasses[variant],
          'animate-in fade-in slide-in-from-top-1 duration-200',
          className
        )}
      >
        {finalIcon && <div className="mt-0.5">{finalIcon}</div>}

        <div className="flex-1 space-y-1">
          {errorList.map((msg, index) => (
            <div key={index} className="text-sm">
              {msg}
            </div>
          ))}
        </div>

        {dismissible && (
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-0.5 hover:bg-base-content/10 rounded transition-colors"
            aria-label="Dismiss error"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }
);

ErrorMessage.displayName = 'ErrorMessage';
